import videojs from 'video.js';

const defaultOptions = {
  chatHost: null,
  chatRoom: null
};

const MANUAL_CLOSE_CODE = 4999;
const MANUAL_CLOSE_REASON = 'manual';

export default class ChatApi extends videojs.EventTarget {
  constructor(player, options) {
    super();
    this.connectsCount = 0;
    this.player = player;
    this.options = videojs.mergeOptions(defaultOptions, options);
  }

  get userAuthorized() {
    return this._userAuthorized;
  }

  set userAuthorized(userAuthorized) {
    this._userAuthorized = userAuthorized;
    let event;
    if (userAuthorized) {
      event = {
        type: 'chatAuthorized',
        data: this.userData
      }
      this.trigger(event);
    } else {
      this.userData = null;
      event = {
        type: 'deauthorized'
      };
      this.trigger(event);
    }
  }

  socialNetworkDeauthorized() {
    this.userAuthorized = false;
  }

  socialNetworkError(errorData) {
    let event = {
      type: 'sn_auth_error',
      data: errorData
    }
    this.trigger(event);
    this.player.trigger(event);
  }

  userAuthorize(userData, type) {
    let data = {
      action: 'authorize',
      type: type || 'local'
    };
    this.userData = userData;
    data = videojs.mergeOptions(data, userData);
    this.send(data);
  }

  sendMessage({ messageText, replyMessageId, attachedFileNames }) {
    if (!this.userAuthorized) {
      return;
    }
    let data = {
      action: 'addMessage',
      text: messageText,
      attachments: attachedFileNames
    };
    if (replyMessageId) {
      data.replyFor = Number(replyMessageId);
    }
    return this.send(data);
  }

  sendLike() {
    return this.send({ action: 'addLike' });
  }

  loadHistory(historyLength) {
    let data = {
      action: 'getHistory',
      length: historyLength
    };
    this.send(data);
  }

  sendBlob(blob) {
    return this.send(blob);
  }

  /**
   * Отправка данных на сервер чата
   * @param {Object} message Данные, отправляемые на сервер чата
   * @returns {Boolean} Успешность операции отправки даннхы на сервер чата
   */
  send(message) {
    if (this._socket && this._socket.readyState === WebSocket.OPEN) {
      this._socket.send(message instanceof Blob ? message : JSON.stringify(message));
      return true;
    }
    return false;
  }

  connect() {
    if (!this.options.chatHost || !this.options.chatRoom) {
      this._log('Unable connect to server');
      return;
    }

    const chatUrl = `${this.options.chatHost}/${this.options.chatRoom}`;
    const socket = new WebSocket(chatUrl);

    socket.addEventListener('open', () => {
      this.connectsCount += 1;

      this._log('Connection to server established');
      // Если ранее пользователь был авторизован, осуществляем повторную авторизацию
      if (this.userAuthorized && this.userData) {
        this.userAuthorize(this.userData);
      }
      this.trigger('connected');
      if (this.connectsCount > 1) {
        this.trigger('reconnected');
      }
    });

    socket.addEventListener('error', (error) => {
      this._log(`Connection error: ${JSON.stringify(error)}`);
    });

    socket.addEventListener('close', (event) => {
      // Если это было закрытие соединения вручную, то игнорируем событие
      if (event.code === MANUAL_CLOSE_CODE || event.reason === MANUAL_CLOSE_REASON) {
        return;
      }
      // Закрываем соединение к серверу вручную, чтобы не создавать много висящих попусту коннектов
      if (this._socket && this._socket.readyState !== WebSocket.OPEN) {
        this._socket.close(MANUAL_CLOSE_CODE, MANUAL_CLOSE_REASON);
      }
      this._log('Connection with server was closed. Try reconnect');
      this._socket = null;
      setTimeout(() => this.connect(), 1000);
    });

    socket.addEventListener('message', (event) => {
      let data = JSON.parse(event.data);
      let eventData;

      switch (data.action) {
        case 'imageAdded':
          this.trigger({
            type: 'imageAdded',
            filename: data.filename
          });
          break;
        case 'newLike':
          this.trigger({ type: 'newLike' });
          break;
        case 'authorizeAnswer':
          this.userAuthorized = true;
          eventData = {
            type: 'authorizeAnswer',
            data: data
          }
          this.trigger(eventData);
          break;
        case 'newMessage':
          eventData = {
            type: 'newMessage',
            data: data.message
          }
          this.trigger(eventData);
          break;
        case 'pinnedMessage':
          eventData = {
            type: 'pinnedMessage',
            data: data.message
          }
          this.trigger(eventData);
          break;
        case 'pinMessage':
          eventData = {
            type: 'pinMessage',
            data: data.message
          }
          this.trigger(eventData);
          break;
        case 'unpinMessage':
          this.trigger({ type: 'unpinMessage' });
          break;
        case 'changeMessage':
          eventData = {
            type: 'changeMessage',
            data: {
              messageId: data.messageId,
              published: data.published,
              message: data.message
            }
          }
          this.trigger(eventData);
          break;
        case 'history':
          eventData = {
            type: 'history',
            data: {
              messages: data.messages
            }
          }
          this.trigger(eventData);
          break;
        case 'addMessageAnswer':
          eventData = {
            type: 'addMessageError',
            data: data
          }
          this.trigger(eventData);
          break;
        case 'unblockedAuthors':
          eventData = {
            type: 'unblockedAuthors',
            data: data
          }
          this.trigger(eventData);
          break;
        case 'blockAuthor':
          eventData = {
            type: 'blockAuthor',
            data: data
          }
          this.trigger(eventData);
          break;
        default:
          this._log(`Get unprocessed message ${data.action}`);
          break;
      }
    });

    this._socket = socket;
  }

  toggleAuthPopup(emitter) {
    this.chatPopup.toggleVisibility(emitter);
  }

  toggleAuthForm(action) {
    this.chatPopup.enableDisableForm(action);
  }

  _log(message) {
    console.log(`[ChatAPI][${new Date().toISOString()}] ${message}`);
  }
};
